import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { TextField, Autocomplete, Grid2, IconButton, Stack } from '@mui/material';
import { PartnerDto, PartnersRycApiClient } from '../../Api/ApiServer';
import { useApi } from '../../Api/useApi';
import { Clear, Close, Menu } from '@mui/icons-material';
import PartnerSelectDetailDialog from './PartnerSelectDetailDialog';

interface PartnerSelectComponentProps {
    onPartnerSelect: (partner: PartnerDto | null, isLoading?: boolean) => void;
    selectedPartnerId?: number;
    title: string;
    onRefresh: () => void;
}

export interface PartnerSelectComponentRef {
    loadPartners: () => Promise<void>;
}

const PartnerSelectComponent = forwardRef<PartnerSelectComponentRef, PartnerSelectComponentProps>(
    ({ onPartnerSelect, title, selectedPartnerId, onRefresh }, ref) => {
    const [selectedPartner, setSelectedPartner] = useState<PartnerDto | null>(null);
    const [partners, setPartners] = useState<PartnerDto[]>([]);
    const [detailOpen, setDetailOpen] = useState(false);
    const { authorizedApi } = useApi(PartnersRycApiClient);


    const loadPartners = React.useCallback(async () => {
        const partners: PartnerDto[] = (await authorizedApi?.getPartners())?.partners ?? [];
        setPartners(partners);
    }, [authorizedApi]);

    useImperativeHandle(ref, () => ({
        loadPartners
    }), [loadPartners]);

    React.useEffect(() => {
        loadPartners();
    }, [loadPartners]);

    React.useEffect(() => {
        if (selectedPartnerId) {
            setSelectedPartner(partners.find(p => p.id === selectedPartnerId) ?? null);
            onPartnerSelect(partners.find(p => p.id === selectedPartnerId) ?? null, true);
        }
    }, [selectedPartnerId, partners, onPartnerSelect]);

    const handlePartnerChange = (event: any, newValue: PartnerDto | null) => {
        setSelectedPartner(newValue);
        onPartnerSelect(newValue);
    };

    return (
        <Stack direction={'row'} display={'flex'} alignItems={'end'}>
            <Autocomplete
                fullWidth
                clearIcon={<Close />}
                value={selectedPartner}
                onChange={handlePartnerChange}
                options={partners}
                getOptionLabel={(option) => option.name ?? ''}
                renderInput={(params) => <TextField {...params} fullWidth label={title} />}
            />
            <IconButton onClick={() => setDetailOpen(true)}>
                <Menu />
            </IconButton>
            <PartnerSelectDetailDialog onClose={() => setDetailOpen(false)} open={detailOpen} partners={partners} onSelect={(p) => { handlePartnerChange(undefined, p); setDetailOpen(false); }} onRefresh={onRefresh} />
        </Stack>
    );
});

export default PartnerSelectComponent;